import { createSlice } from "@reduxjs/toolkit";
import { apiSlice } from "../../app/api/apiSlice";

const initialState = {
	pendingRequestIds: {},
};

const apiRequestStatusSlice = createSlice({
	name: "apiRequestStatus",
	initialState,
	reducers: {
		trackRequestStarted(state, action) {
			const requestId = String(action?.payload?.requestId ?? "").trim();

			if (!requestId) {
				return;
			}

			state.pendingRequestIds[requestId] = true;
		},
		trackRequestFinished(state, action) {
			const requestId = String(action?.payload?.requestId ?? "").trim();

			if (!requestId) {
				return;
			}

			delete state.pendingRequestIds[requestId];
		},
		resetTrackedRequests() {
			return initialState;
		},
	},
});

export const {
	trackRequestStarted,
	trackRequestFinished,
	resetTrackedRequests,
} = apiRequestStatusSlice.actions;

export const selectPendingApiRequestCount = (state) =>
	Object.keys(state?.apiRequestStatus?.pendingRequestIds ?? {}).length;

export const apiRequestStatusMiddleware = (storeApi) => (next) => (action) => {
	const actionType = String(action?.type ?? "");
	const endpointName = action?.meta?.arg?.endpointName;
	const requestId = action?.meta?.requestId;
	const isTrackedApiAction =
		Boolean(endpointName) &&
		actionType.startsWith(`${apiSlice.reducerPath}/`) &&
		Boolean(requestId);

	if (isTrackedApiAction) {
		if (actionType.endsWith("/pending")) {
			storeApi.dispatch(trackRequestStarted({ requestId }));
		}

		if (
			actionType.endsWith("/fulfilled") ||
			actionType.endsWith("/rejected")
		) {
			storeApi.dispatch(trackRequestFinished({ requestId }));
		}
	}

	return next(action);
};

export const waitForPendingApiRequestsToSettle = (
	store,
	{
		settleDelayMs = 200,
		timeoutMs = 15000,
	} = {}
) =>
	new Promise((resolve) => {
		let settleTimer = null;
		let timeoutTimer = null;
		let finished = false;

		const cleanup = () => {
			if (finished) {
				return;
			}

			finished = true;

			if (settleTimer) {
				clearTimeout(settleTimer);
			}

			if (timeoutTimer) {
				clearTimeout(timeoutTimer);
			}

			unsubscribe?.();
		};

		const finish = () => {
			cleanup();
			resolve(true);
		};

		const maybeStartSettleTimer = () => {
			if (settleTimer) {
				return;
			}

			settleTimer = setTimeout(finish, settleDelayMs);
		};

		const checkPendingCount = () => {
			const pendingCount = selectPendingApiRequestCount(store.getState());

			if (pendingCount <= 0) {
				maybeStartSettleTimer();
				return;
			}

			if (settleTimer) {
				clearTimeout(settleTimer);
				settleTimer = null;
			}
		};

		const unsubscribe = store.subscribe(checkPendingCount);

		timeoutTimer = setTimeout(() => {
			console.warn(
				"[apiRequestStatus] waitForPendingApiRequestsToSettle timed out"
			);
			finish();
		}, timeoutMs);

		checkPendingCount();
	});

export default apiRequestStatusSlice.reducer;
